import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Trophy, CalendarCheck, ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Upcoming league games for this parent's children — no fees involved. */
export const ParentFixturesCard = () => {
  const [fixtures, setFixtures] = useState([]);
  const [records, setRecords] = useState([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    axios.get(`${API}/academy/parent/fixtures`, { withCredentials: true })
      .then((r) => {
        setFixtures(r.data.fixtures || []);
        setRecords(r.data.records || []);
        setNote(r.data.fee_note || "");
      })
      .catch(() => {});
  }, []);

  if (fixtures.length === 0 && records.length === 0) return null;

  return (
    <Card className="bg-slate-900/60 border-amber-500/30" data-testid="parent-fixtures-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-amber-200">
          <Trophy className="w-5 h-5" /> Upcoming games
        </CardTitle>
        <p className="text-xs text-slate-400">{note}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {records.length > 0 && (
          <div className="space-y-2 mb-2" data-testid="parent-league-record">
            {records.map((t) => (
              <div key={t.team_id} className="rounded-lg border border-slate-700 bg-slate-900/60 p-3">
                <p className="text-xs text-white">
                  {t.team_name}
                  <span className="text-slate-500"> · {(t.players || []).join(", ")}</span>
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Played {t.played} · Won {t.won} · Drew {t.drawn} · Lost {t.lost} · Goals{" "}
                  {t.goals_for}-{t.goals_against} ·
                  <span className="text-white font-mono"> {t.points} pts</span>
                </p>
                {(t.results || []).map((f) => (
                  <p key={f.id} className="text-[11px] text-slate-500 mt-0.5">
                    {f.date} · {t.team_name} {f.result.our_score}–{f.result.their_score} {f.opponent}
                    {f.result.scorers ? ` · ${f.result.scorers}` : ""}
                  </p>
                ))}
              </div>
            ))}
          </div>
        )}

        {fixtures.map((f) => (
          <div key={f.id} className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2"
            data-testid={`parent-fixture-${f.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-white">
                {f.team_name} <span className="text-slate-500">vs</span> {f.opponent}
              </p>
              <Badge className={`text-[10px] ${
                f.status === "cancelled" ? "bg-red-500/15 text-red-300 border-red-500/40"
                : f.status === "postponed" ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
                : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"}`}>
                {f.status}
              </Badge>
            </div>
            <p className="text-xs text-slate-400">
              {[f.label, f.start_time, f.venue, f.home_away].filter(Boolean).join(" · ")}
            </p>
            {f.players?.length > 0 && (
              <p className="text-[11px] text-slate-500">{f.players.join(", ")}</p>
            )}
            {f.selected_players?.length > 0 && (
              <p className="text-[11px] text-emerald-300 mt-1" data-testid={`parent-fixture-squad-${f.id}`}>
                {f.selected_players.join(", ")} selected
                {f.meet_time ? ` · meet ${f.meet_time}` : ""}
                {f.squad_note ? ` · ${f.squad_note}` : ""}
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

/** "Can your child make it?" for the next training dates. */
export const ParentAvailabilityCard = () => {
  const [children, setChildren] = useState([]);
  const [dates, setDates] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/academy/parent/session-rsvps`, { withCredentials: true });
      setChildren(r.data.children || []);
      const byChild = {};
      for (const c of r.data.children || []) {
        byChild[c.player_id] = (c.upcoming || []).slice(0, 6);
      }
      setDates(byChild);
    } catch {
      /* dashboard card: stay quiet if it can't load */
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const answer = async (child, date, attending) => {
    setBusy(`${child.player_id}-${date}`);
    try {
      await axios.post(`${API}/academy/session-rsvp`, {
        player_id: child.player_id, date, attending,
      }, { withCredentials: true });
      toast.success(attending ? "Thanks — see you there" : "Thanks for letting us know");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that");
    } finally {
      setBusy("");
    }
  };

  const anyDates = Object.values(dates).some((d) => d.length > 0);
  if (children.length === 0 || !anyDates) return null;

  return (
    <Card className="bg-slate-900/60 border-slate-700" data-testid="parent-availability-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarCheck className="w-5 h-5 text-emerald-400" /> Can they make it?
        </CardTitle>
        <p className="text-xs text-slate-400">
          Tell the coach in advance and we&apos;ll know who to expect. No need to wait for the group chat.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {children.map((c) => (
          <div key={c.player_id} data-testid={`availability-child-${c.player_id}`}>
            <p className="text-sm text-white mb-1">{c.player_name}</p>
            <div className="space-y-1.5">
              {(dates[c.player_id] || []).map((s) => {
                const saved = c.answers?.[s.date];
                const key = `${c.player_id}-${s.date}`;
                return (
                  <div key={s.date} className="flex items-center justify-between gap-2 rounded bg-slate-950/60 border border-slate-800 px-2 py-1.5">
                    <span className="text-xs text-slate-300 min-w-0 truncate">
                      {s.label}{s.start_time ? ` · ${s.start_time}` : ""}
                    </span>
                    <div className="flex items-center gap-1 shrink-0">
                      {busy === key ? (
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                      ) : (
                        <>
                          <Button size="sm" variant={saved?.attending === true ? "default" : "outline"}
                            className={`h-8 px-2 text-[11px] ${saved?.attending === true ? "bg-emerald-600 hover:bg-emerald-500" : ""}`}
                            onClick={() => answer(c, s.date, true)}
                            data-testid={`rsvp-yes-${c.player_id}-${s.date}`}>
                            <ThumbsUp className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant={saved?.attending === false ? "default" : "outline"}
                            className={`h-8 px-2 text-[11px] ${saved?.attending === false ? "bg-amber-600 hover:bg-amber-500" : ""}`}
                            onClick={() => answer(c, s.date, false)}
                            data-testid={`rsvp-no-${c.player_id}-${s.date}`}>
                            <ThumbsDown className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
